import { useEffect, useState } from 'react';
import { Pack, Tab, User } from '../types';
import { packs as packsApi, user as userApi } from '../api';

interface HomeProps {
  user: User;
  onTabChange: (tab: Tab) => void;
}

export default function Home({ user, onTabChange }: HomeProps) {
  const [featuredPack, setFeaturedPack] = useState<Pack | null>(null);
  const [stats, setStats] = useState({ card_count: 0, portfolio_value_usd: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      // Load packs and user stats independently — don't let one block the other
      packsApi.list()
        .then((res) => setFeaturedPack(res.packs[0] ?? null))
        .catch(console.error);

      userApi.me()
        .then((res) => setStats((res as { stats?: { card_count: number; portfolio_value_usd: number } }).stats ?? { card_count: 0, portfolio_value_usd: 0 }))
        .catch(() => setStats({ card_count: 0, portfolio_value_usd: 0 }))
        .finally(() => setLoading(false));
    };
    load();
  }, []);

  return (
    <div className="h-full scroll-area px-4 py-4 space-y-5">
      {/* Welcome */}
      <div>
        <h2 className="text-white text-2xl font-bold">
          Hey, {user.tg_name.split(' ')[0]} 👋
        </h2>
        <p className="text-gray-400 text-sm mt-0.5">
          Build your ultimate Pokémon collection
        </p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          label="Cards Owned"
          value={loading ? '—' : String(stats.card_count)}
          icon="🃏"
          color="purple"
        />
        <StatCard
          label="Portfolio Value"
          value={loading ? '—' : `$${stats.portfolio_value_usd.toFixed(2)}`}
          icon="📈"
          color="gold"
        />
      </div>

      {/* Featured pack */}
      {featuredPack && (
        <div className="bg-gradient-to-br from-accent-purple/30 to-surface-elevated rounded-2xl p-4 border border-accent-purple/20">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wide">Featured Pack</p>
              <h3 className="text-white font-bold text-xl mt-0.5">{featuredPack.name}</h3>
            </div>
            <div className="text-4xl">📦</div>
          </div>

          {/* Pack contents preview */}
          <div className="flex gap-1 mb-4">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className={`flex-1 aspect-[1/1.4] rounded-md ${
                  i === 9
                    ? 'bg-gradient-to-br from-amber-500 to-yellow-300'
                    : 'bg-blue-900/60 border border-blue-700/30'
                } flex items-center justify-center text-xs`}
              >
                {i === 9 ? '⭐' : ''}
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-accent-gold font-bold text-2xl">
                💎 {featuredPack.price_ton} TON
              </p>
              <p className="text-gray-400 text-xs">10 cards · 1 guaranteed rare</p>
            </div>
            <button
              onClick={() => onTabChange('packs')}
              className="bg-accent-purple hover:bg-accent-purple/80 text-white font-bold px-5 py-2.5 rounded-xl transition-colors shadow-lg shadow-accent-purple/30"
            >
              Open Now
            </button>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <QuickAction
          icon="🛒"
          title="Marketplace"
          subtitle="Browse listings"
          onClick={() => onTabChange('marketplace')}
          color="blue"
        />
        <QuickAction
          icon="🃏"
          title="Collection"
          subtitle="View your cards"
          onClick={() => onTabChange('collection')}
          color="green"
        />
      </div>

      {/* Rarity guide */}
      <div className="bg-surface-card rounded-xl p-4 space-y-2 border border-white/5">
        <h4 className="text-gray-300 font-semibold text-sm">Rarity Weights</h4>
        <div className="space-y-1.5">
          <RarityBar label="Common" pct={60} color="bg-gray-500" />
          <RarityBar label="Uncommon" pct={25} color="bg-emerald-600" />
          <RarityBar label="Rare" pct={12} color="bg-blue-600" />
          <RarityBar label="Ultra Rare" pct={3} color="bg-amber-500" />
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string;
  icon: string;
  color: 'purple' | 'gold';
}) {
  return (
    <div
      className={`bg-surface-card rounded-xl p-3 border ${
        color === 'purple' ? 'border-accent-purple/20' : 'border-accent-gold/20'
      }`}
    >
      <div className="flex items-center justify-between mb-1">
        <p className="text-gray-400 text-xs">{label}</p>
        <span className="text-lg">{icon}</span>
      </div>
      <p
        className={`font-bold text-xl ${
          color === 'purple' ? 'text-accent-purple-light' : 'text-accent-gold'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function QuickAction({
  icon,
  title,
  subtitle,
  onClick,
  color,
}: {
  icon: string;
  title: string;
  subtitle: string;
  onClick: () => void;
  color: 'blue' | 'green';
}) {
  return (
    <button
      onClick={onClick}
      className={`bg-surface-card rounded-xl p-3 border text-left active:scale-95 transition-transform ${
        color === 'blue' ? 'border-blue-800/30' : 'border-emerald-800/30'
      }`}
    >
      <span className="text-2xl">{icon}</span>
      <p className="text-white font-semibold text-sm mt-1">{title}</p>
      <p className="text-gray-500 text-xs">{subtitle}</p>
    </button>
  );
}

function RarityBar({ label, pct, color }: { label: string; pct: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-gray-400 text-xs w-20">{label}</span>
      <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-gray-500 text-xs w-8 text-right">{pct}%</span>
    </div>
  );
}
